import { useCallback, useState } from "react";
import { useLocation } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Button,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useDeleteEntityView } from "#app/lib/hooks/use-entity-views";
import { AnalyticsContext, DashboardEvent, trackEvent } from "#app/lib/analytics";
import type { DashboardSurface } from "./use-dashboard-surfaces";

type DeleteDashboardDialogProps = Omittable<{
  /** The dashboard to delete. Null keeps the dialog closed. */
  surface: DashboardSurface | null;
  onOpenChange: (open: boolean) => void;
}>;

/**
 * The confirm step for deleting a dashboard from OUTSIDE its host — the
 * directory table's row menu and the module menu's rows, which act on a
 * dashboard in whichever scope holds it (a dashboard's own tab confirms
 * through `DeleteViewDialog`, inside its scope's views provider).
 *
 * The copy names the audience the delete reaches: a workflow dashboard is a
 * tab everyone in the workspace sees; a workspace dashboard is the viewer's
 * own row plus whoever it is shared with. When the viewer is standing on the
 * dashboard being deleted (the module menu is reachable from its page), the
 * delete lands them on the directory — the URL outlives the row otherwise.
 */
export function DeleteDashboardDialog({
  surface,
  onOpenChange,
}: DeleteDashboardDialogProps) {
  const deleteEntityView = useDeleteEntityView();
  const { replace } = useAppNavigate();
  const { pathname } = useLocation();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = useCallback(async () => {
    if (!surface) return;
    setDeleting(true);
    try {
      const ok = await deleteEntityView(
        { kind: surface.scope.kind, id: surface.scope.id },
        surface.view.id,
      );
      if (!ok) return;
      trackEvent({
        context: AnalyticsContext.Dashboard,
        event: DashboardEvent.Delete,
        data: { via: "row_menu", scope: surface.scope.kind },
      });
      onOpenChange(false);
      if (pathname.endsWith(surface.route)) replace("/dashboards");
    } finally {
      setDeleting(false);
    }
  }, [surface, deleteEntityView, onOpenChange, pathname, replace]);

  const isWorkflowDashboard = surface?.scope.kind === "workflow";

  return (
    <AlertDialog open={surface != null} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete &ldquo;{surface?.name}&rdquo;?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isWorkflowDashboard
              ? "This dashboard is a tab on its workflow, shared with everyone in the workspace. Deleting it removes the tab and its widgets for all of them."
              : "Deleting this dashboard removes it and its widgets for you and everyone it is shared with."}{" "}
            The Projects it showed are not affected.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose
            render={<Button variant="outline" />}
            disabled={deleting}
          >
            Cancel
          </AlertDialogClose>
          <Button
            color="destructive"
            onClick={() => {
              void handleDelete();
            }}
            disabled={deleting}
            loading={deleting}
          >
            Delete dashboard
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
